import { query, where } from '@/utils/firebase/lazyFirestore'
import chunk from 'lodash-es/chunk'
import merge from 'lodash-es/merge'

import { AgreementType } from '#shared/types/Agreement'
import type { Agreement, Agreements } from '#shared/types/Agreement'
import type { Job, JobFormData, JobId } from '#shared/types/Job'
import {
    JobContentMode,
    JobFeeType,
    JobPlaceType,
    JOBS_DEFAULT_VISIBILITY,
    JobTimeType,
    JobType,
    StartType
} from '#shared/types/Job'
import type { JobRequest } from '#shared/types/JobRequest'
import type { OrgId } from '#shared/types/ids'
import type { ConsentData, Org } from '#shared/types/Org'
import { OrgType } from '#shared/types/Org'
import type { UserId } from '#shared/types/ids'
import { VisibilityChangeAction, VisibilityType } from '#shared/types/visibility'
import { formatDate, orderByDate } from '#shared/utils'

import { useConsents } from '@/composables/consents/useConsents'
import { useAgreement } from '@/composables/agreement/useAgreement'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useFirebase } from '@/composables/firebase/useFirebase'
import { useSemanticJobSearch } from '@/composables/jobs/useSemanticJobSearch'
import { useCheese } from '@/composables/useCheese'

export const useJobs = () => {
    // `collections` resolves each reference on access, so this works before Firestore is loaded.
    const { $firebase, collections } = useFirebase()
    const { $api } = useNuxtApp()

    // Safely destructure Firebase, providing fallbacks for SSR
    const listDocuments = $firebase?.listDocuments
    const loadDocumentByAttrOrId = $firebase?.loadDocumentByAttrOrId
    const loadDocumentOrFail = $firebase?.loadDocumentOrFail
    const updateDocument = $firebase?.updateDocument

    const { giveConsent } = useConsents()
    const { track: trackCheese } = useCheese()
    const { loadActualDocumentVersion, loadAgreement } = useAgreement()
    const { keywordSearch, loadFacets } = useTypesense()
    const { searchJobsSemanticaly, searchJobsPureSemantic } = useSemanticJobSearch()

    // We'll use the useOrgs composable to access Firebase directly
    const { loadUserOrgs, fetchGovOrgs } = useOrgs()

    const orgsService = {
        fetchGovOrgs,
        loadUserOrgs,
        saveConsentForOrg: async (orgId: OrgId, userId: UserId, agreement: Agreement) => {
            return await $api.post(`orgs/${orgId}/consents`, { userId, agreement })
        }
    }

    // Helper to ensure Firebase is available (throws on client if not initialized)
    const ensureFirebase = () => {
        if (import.meta.client && !$firebase) {
            throw new Error('Firebase not initialized')
        }
    }

    const DEFAULT_PER_PAGE = 24
    const JOBS_STORAGE_KEY = 'view'

    const deleteJobFromCollection = async (jobID: string) => {
        return await $api.post(`jobs/${jobID}/delete`)
    }

    const archiveJob = async (jobId: JobId): Promise<void> => {
        ensureFirebase()
        await updateDocument!(collections!.jobs, jobId, { deletedAt: new Date() } as Partial<Job>, {
            withAuthor: true,
            withTimestamps: true
        })
    }

    const restoreJob = async (jobId: JobId): Promise<void> => {
        ensureFirebase()
        // null clears the soft-delete flag — listings/Typesense use a truthy check on deletedAt
        await updateDocument!(collections!.jobs, jobId, { deletedAt: null } as any, {
            withAuthor: true,
            withTimestamps: true
        })
    }

    const searchJobs = async (query: string, options: Partial<any> = {}) => {
        return await keywordSearch('jobs', query, {
            ...options,
            per_page: options.hitsPerPage || DEFAULT_PER_PAGE
        })
    }

    const loadJobsFacets = async (options: { facets?: string[]; filters?: string } = {}): Promise<any> => {
        return await loadFacets('jobs', options)
    }

    const loadJobOrganizations = async (options: { filters?: string } = {}): Promise<Array<{ id: string; label: string }>> => {
        const organizations = new Map<string, string>()
        let page = 0
        let nbPages = 1

        while (page < nbPages) {
            const result = await keywordSearch('jobs', '*', {
                page,
                per_page: 250,
                filterBy: options.filters
            })

            for (const hit of result.hits as any[]) {
                const orgId = String(hit.orgId || hit.orgMeta?.key || '').trim()
                if (!orgId) continue

                const orgName = String(hit.orgName || hit.orgMeta?.title || orgId).trim() || orgId
                if (!organizations.has(orgId)) {
                    organizations.set(orgId, orgName)
                }
            }

            nbPages = result.nbPages || 0
            page += 1
        }

        return [...organizations.entries()]
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'sk', { sensitivity: 'base' }))
    }

    const getFeaturedJobs = () => {
        return keywordSearch('jobs', '', {
            per_page: 50
        }).then((results: any) => results.hits)
    }

    const getFeaturedGovernmentJobs = async (jobs: Job[]) => {
        const govOrgIds = (await orgsService.fetchGovOrgs()).map((org: Org) => org.id)

        return jobs.filter((item) => {
            return govOrgIds.includes(item.orgId)
        })
    }

    const isJobTypePaidService = (org?: Org, job?: JobFormData) => {
        if (!org?.type || !job?.type) return false

        return (
            [OrgType.privateSector, OrgType.startUp].includes(org!.type! as any) &&
            (job!.type!.includes(JobType.partTime) || job!.type!.includes(JobType.fullTime))
        )
    }

    const isJobTypeInternship = (org?: Org, job?: JobFormData) => {
        if (!org?.type || !job?.type) return false

        return [OrgType.privateSector, OrgType.startUp].includes(org.type) && job.type.includes(JobType.internship)
    }

    const isJobFeeTypeAdvertising = (org?: Org, job?: JobFormData) => {
        if (!org?.type || !job?.type) return false

        return [OrgType.privateSector, OrgType.startUp].includes(org.type) && job.feeType === JobFeeType.ADVERTISING
    }

    const isJobFlexi = (job?: JobFormData) => {
        if (!job) return false

        return (
            !!job.jobTime?.includes(JobTimeType.flexible) ||
            !!job.jobPlace?.includes(JobPlaceType.homeoffice) ||
            !!job.jobPlace?.includes(JobPlaceType.hybrid)
        )
    }

    const isJobTypePaid = (salaryMin: number, salaryMax: number) => {
        salaryMin = Number(salaryMin)
        salaryMax = Number(salaryMax)
        return (salaryMin === salaryMax && salaryMin !== 0) || salaryMin !== 0 || salaryMax !== 0
    }

    const hasJobSalarySet = (salaryMin: number, salaryMax: number, langPrefixFrom: string, langPrefixTo: string) => {
        return salaryMin && salaryMax === 0
            ? langPrefixFrom + ' ' + salaryMin + ' €'
            : salaryMin === 0 && salaryMax
              ? langPrefixTo + ' ' + salaryMax + ' €'
              : salaryMin === salaryMax
                ? salaryMin + ' €'
                : [salaryMin, salaryMax + ' €'].filter(Boolean).join(' - ')
    }

    const formatJobSalary = (
        jobType: NonNullable<Job['type']>,
        salaryMin: number,
        salaryMax: number,
        langInternship: string,
        langCompensationNotSpecified: string,
        langPrefixFrom: string,
        langPrefixTo: string,
        langUnit: string
    ) => {
        salaryMin = Number(salaryMin)
        salaryMax = Number(salaryMax)

        if (!isJobTypePaid(salaryMin, salaryMax) && jobType.length === 1 && jobType.includes(JobType.internship)) {
            return langInternship
        }

        if ((!salaryMin && !salaryMax) || !langUnit) return langCompensationNotSpecified

        if (salaryMin && salaryMax) {
            return `${salaryMin} - ${salaryMax} ${langUnit}`
        }

        const fromSalary = !!salaryMin && `${langPrefixFrom} ${salaryMin}`
        const toSalary = !!salaryMax && `${langPrefixTo} ${salaryMax}`

        const salary = [fromSalary, toSalary].filter(Boolean)
        if (!salary.length) return langCompensationNotSpecified

        return `${salary.join(' ')} ${langUnit}`
    }

    const jobSalaryRate = (
        jobType: NonNullable<Job['type']>,
        salaryMin: number,
        salaryMax: number,
        langInternship: string,
        langCompensationNotSpecified: string,
        langPrefixFrom: string,
        langPrefixTo: string
    ) => {
        salaryMin = Number(salaryMin)
        salaryMax = Number(salaryMax)

        if (!isJobTypePaid(salaryMin, salaryMax) && jobType.includes(JobType.internship)) {
            return langInternship
        }

        return hasJobSalarySet(salaryMin, salaryMax, langPrefixFrom, langPrefixTo) || langCompensationNotSpecified
    }

    const formatStartDate = (job: Job, rightAwayText: string = 'ihneď'): string => {
        if (!job.startType) {
            return ''
        }

        if (job.startType === StartType.DATE && !job.startDate) {
            return ''
        }

        return job.startType === StartType.DATE
            ? formatDate(job.startDate, {
                  format: job.lang === 'en' ? 'D MMMM YYYY' : 'D. MMMM YYYY',
                  locale: job.lang === 'en' ? 'en' : 'sk'
              })
            : rightAwayText
    }

    const formatEndDate = (job: Job, rightAwayText: string = 'ihneď'): string => {
        if (!job.endDate) {
            return ''
        }

        return (
            formatDate(job.endDate, {
                format: job.lang === 'en' ? 'D MMMM YYYY' : 'D. MMMM YYYY',
                locale: job.lang === 'en' ? 'en' : 'sk'
            }) || rightAwayText
        )
    }

    const loadJob = (id: JobId) => {
        ensureFirebase()
        return loadDocumentOrFail!<Job>(collections!.jobs, id)
    }

    const loadJobBySlugOrId = (slug: string) => {
        ensureFirebase()
        return loadDocumentByAttrOrId!<Job>(collections!.jobs, 'slug', slug)
    }

    const loadJobDrafts = async (): Promise<any> => {
        ensureFirebase()
        const userOrgs = await orgsService.loadUserOrgs()

        const resp = await Promise.all(
            userOrgs.map(async (org: Org) => {
                return await listDocuments!<Job>(
                    query(collections!.jobs, where('orgId', '==', org.id), where('isDraft', '==', true))
                )
            })
        )

        return resp.flat()
    }

    const listJobsByOrgId = async (orgId: OrgId | OrgId[]): Promise<Job[]> => {
        ensureFirebase()
        const orgIds = ([] as OrgId[]).concat(orgId)

        // XXX: Firestore query only support where-in clause with up to 10 values to compare
        const chunkedOrgIds = chunk(orgIds, 10)
        const chunkedPromises = chunkedOrgIds.map((orgIds) => {
            return listDocuments!<Job>(query(collections!.jobs, where('orgId', 'in', orgIds)))
        })

        const jobs = (await Promise.all(chunkedPromises)).flat()

        return orderByDate(jobs)
    }

    const upsertJob = async (
        data: JobFormData,
        userId: UserId,
        jmc?: Agreement,
        imc?: Agreement,
        jatac?: Agreement
    ): Promise<Job> => {
        ensureFirebase()
        const isDraft = data.isDraft || data.visibility === VisibilityType.draft
        const consentCreator = { user: { id: userId } }

        const jmcConsent = !isDraft && !!jmc && (await giveConsent(jmc, consentCreator))
        const imcConsent = !isDraft && !!imc && (await giveConsent(imc, consentCreator))
        const jatacConsent = !isDraft && !!jatac && (await giveConsent(jatac, consentCreator))

        const jobData: JobFormData = {
            ...data,
            consents: {
                ...(!isDraft && !!data.id && data.consents),

                ...(jmcConsent && {
                    [AgreementType.jobMediationContract]: {
                        accepted: true,
                        consentId: jmcConsent.id,
                        agreementId: jmc.id
                    }
                }),

                ...(imcConsent && {
                    [AgreementType.internshipMediationContract]: {
                        accepted: true,
                        consentId: imcConsent.id,
                        agreementId: imc.id
                    }
                }),

                ...(jatacConsent && {
                    [AgreementType.jobAdvertisingTermsAndConditions]: {
                        accepted: true,
                        consentId: jatacConsent.id,
                        agreementId: jatac.id
                    }
                })
            }
        }

        const response = await $api.post<Job>('jobs/upsert', jobData)
        if (!data.id) trackCheese('job_created', { job_id: response.data.id, org_id: data.orgId || '' })

        if (jmcConsent) {
            await updateDocument!(collections!.consents, jmcConsent.id, {
                jobId: response.data.id
            })
        }

        if (imcConsent) {
            await updateDocument!(collections!.consents, imcConsent.id, {
                jobId: response.data.id
            })
        }

        if (jatacConsent) {
            await updateDocument!(collections!.consents, jatacConsent.id, {
                jobId: response.data.id
            })
        }

        const job = await loadJob(response.data.id)
        if (!isDraft && job.visibility === VisibilityType.published) {
            trackCheese('job_published', { job_id: job.id, org_id: job.orgId })
        }
        return job
    }

    const updateJob = async (
        jobId: JobId,
        data: JobFormData,
        userId: UserId,
        jmc?: Agreement,
        imc?: Agreement,
        jatac?: Agreement
    ): Promise<Job> => {
        ensureFirebase()
        const isDraft = data.isDraft || data.visibility === VisibilityType.draft
        const consentCreator = { user: { id: userId } }

        const jmcConsent = !isDraft && !!jmc && (await giveConsent(jmc, consentCreator))
        const imcConsent = !isDraft && !!imc && (await giveConsent(imc, consentCreator))
        const jatacConsent = !isDraft && !!jatac && (await giveConsent(jatac, consentCreator))

        const jobData: JobFormData = {
            ...data,
            consents: {
                ...(!isDraft && !!data.id && data.consents),

                ...(jmcConsent && {
                    [AgreementType.jobMediationContract]: {
                        accepted: true,
                        consentId: jmcConsent.id,
                        agreementId: jmc.id
                    }
                }),

                ...(imcConsent && {
                    [AgreementType.internshipMediationContract]: {
                        accepted: true,
                        consentId: imcConsent.id,
                        agreementId: imc.id
                    }
                }),

                ...(jatacConsent && {
                    [AgreementType.jobAdvertisingTermsAndConditions]: {
                        accepted: true,
                        consentId: jatacConsent.id,
                        agreementId: jatac.id
                    }
                })
            }
        }

        const response = await $api.post<Job>(`jobs/${jobId}/update`, jobData)

        if (jmcConsent) {
            await updateDocument!(collections!.consents, jmcConsent.id, {
                jobId: response.data.id
            })
        }

        if (imcConsent) {
            await updateDocument!(collections!.consents, imcConsent.id, {
                jobId: response.data.id
            })
        }

        if (jatacConsent) {
            await updateDocument!(collections!.consents, jatacConsent.id, {
                jobId: response.data.id
            })
        }

        return loadJob(response.data.id)
    }

    const updateJobVisibility = async (
        id: JobId,
        userId: UserId,
        action: VisibilityChangeAction,
        data?: Partial<Job>,
        jmc?: Agreement,
        imc?: Agreement,
        jatac?: Agreement
    ): Promise<Job> => {
        ensureFirebase()
        const consentCreator = { user: { id: userId } }

        const jmcConsent = !!jmc && (await giveConsent(jmc, consentCreator))
        const imcConsent = !!imc && (await giveConsent(imc, consentCreator))
        const jatacConsent = !!jatac && (await giveConsent(jatac, consentCreator))

        const response = await $api.post<Job>(`/jobs/${id}/update-visibility`, {
            action,
            ...data,
            consents: {
                ...data?.consents,

                ...(jmcConsent && {
                    [AgreementType.jobMediationContract]: {
                        accepted: true,
                        consentId: jmcConsent.id,
                        agreementId: jmc.id
                    }
                }),

                ...(imcConsent && {
                    [AgreementType.internshipMediationContract]: {
                        accepted: true,
                        consentId: imcConsent.id,
                        agreementId: imc.id
                    }
                }),

                ...(jatacConsent && {
                    [AgreementType.jobAdvertisingTermsAndConditions]: {
                        accepted: true,
                        consentId: jatacConsent.id,
                        agreementId: jatac.id
                    }
                })
            }
        })

        if (jmcConsent) {
            await updateDocument!(collections!.consents, jmcConsent.id, {
                jobId: response.data.id
            })
        }

        if (imcConsent) {
            await updateDocument!(collections!.consents, imcConsent.id, {
                jobId: response.data.id
            })
        }

        if (jatacConsent) {
            await updateDocument!(collections!.consents, jatacConsent.id, {
                jobId: response.data.id
            })
        }

        if (action === VisibilityChangeAction.PUBLISH) {
            trackCheese('job_published', { job_id: id, org_id: response.data.orgId || '' })
        }
        return loadJob(response.data.id)
    }

    const updateJobNotifiableMembers = async (id: JobId, notifiableMembers: Job['notifiableMembers']): Promise<Job> => {
        const response = await $api.post<Job>(`/jobs/${id}/update-notifiable-members`, {
            notifiableMembers
        })

        return loadJob(response.data.id)
    }

    /**
     * @deprecated Use upsertJob instead
     */
    const createJob = async (
        data: JobFormData,
        userId: UserId,
        orgId: OrgId,
        jobMediationContract?: Agreement,
        internshipMediationContract?: Agreement,
        jobAdvertisingTermsAndConditions?: Agreement
    ): Promise<Job> => {
        ensureFirebase()
        const jobMediationContractConsent =
            !!jobMediationContract && (await giveConsent(jobMediationContract, { user: { id: userId } }))

        const internshipMediationContractConsent =
            !!internshipMediationContract && (await giveConsent(internshipMediationContract, { user: { id: userId } }))

        const jobAdvertisingTermsAndConditionsConsent =
            !!jobAdvertisingTermsAndConditions &&
            (await giveConsent(jobAdvertisingTermsAndConditions, { user: { id: userId } }))

        const jobData: Partial<Job> = {
            ...data,
            orgId,
            createdById: userId,
            consents: {
                ...(jobMediationContractConsent && {
                    [AgreementType.jobMediationContract]: {
                        accepted: true,
                        consentId: jobMediationContractConsent.id,
                        agreementId: jobMediationContract.id
                    }
                }),

                ...(internshipMediationContractConsent && {
                    [AgreementType.internshipMediationContract]: {
                        accepted: true,
                        consentId: internshipMediationContractConsent.id,
                        agreementId: internshipMediationContract.id
                    }
                }),

                ...(jobAdvertisingTermsAndConditionsConsent && {
                    [AgreementType.jobAdvertisingTermsAndConditions]: {
                        accepted: true,
                        consentId: jobAdvertisingTermsAndConditionsConsent.id,
                        agreementId: jobAdvertisingTermsAndConditions.id
                    }
                })
            }
        }

        const response = await $api.post('jobs/create', jobData)

        if (jobMediationContractConsent) {
            await updateDocument!(collections!.consents, jobMediationContractConsent.id, {
                jobId: response.data.id
            })
        }

        if (internshipMediationContractConsent) {
            await updateDocument!(collections!.consents, internshipMediationContractConsent.id, {
                jobId: response.data.id
            })
        }

        if (jobAdvertisingTermsAndConditionsConsent) {
            await updateDocument!(collections!.consents, jobAdvertisingTermsAndConditionsConsent.id, {
                jobId: response.data.id
            })
        }

        return loadJob(response.data.id)
    }

    const updateJobNotifiableMembersFromToken = async (token: string): Promise<void> => {
        await $api.post('/update-notifiable-members/job', {}, { headers: { token } })
    }

    const makeEmptyJob = (override?: Partial<Job>): JobFormData => {
        return {
            title: '',
            type: [],
            formOfCollaboration: [],
            jobPlace: [],
            who: [],
            startType: StartType.ASAP,
            // location: '',
            remote: false,
            consents: {},
            jobLocation: [],
            jobTime: [],
            salaryUnit: 'EUR / mesiac',
            salaryNote: '',
            manager: '',
            managerLinkedin: '',
            motivation: true,
            cv: true,
            content: '',
            contentMode: JobContentMode.STRUCTURED,
            challenge: '',
            desc: '',
            education: '',
            educationDetails: '',
            degree: '',
            topics: [],
            expertise: [],
            languages: [],
            skills: [],
            benefits: '',
            premium: false,
            published: false,
            image: '',
            additionalInfo: '',
            lang: 'sk',
            feeType: JobFeeType.SATISFACTION,
            visibility: JOBS_DEFAULT_VISIBILITY,
            ...override
        }
    }

    const loadJobRequests = async (job: Job) => {
        ensureFirebase()
        return await listDocuments!<JobRequest>(
            query(collections!.jobRequests, where('jobId', '==', job.id), where('orgId', '==', job.orgId))
        )
    }

    const getNotifiableMembersOfJob = async (jobId: string) => {
        const {
            data: { notifiable }
        } = await $api.get(`notifiable-members/job/${jobId}`)
        return notifiable
    }

    const getDataFromJobNotificationMailToken = async (
        token: string
    ): Promise<{ jobId: string; jobSlug?: string; jobTitle: string }> => {
        const {
            data: { job }
        } = await $api.post('/confirmation-of-notification/job', {}, { headers: { token } })

        return { jobId: job.id, jobSlug: job.slug, jobTitle: job.title }
    }

    const loadContract = async (org: Org, agreementType: AgreementType): Promise<Agreement | null> => {
        const consentData = org.consents?.[agreementType as keyof Agreements] as ConsentData | undefined
        if (!consentData?.accepted && org.type !== OrgType.startUp && org.type !== OrgType.privateSector) {
            return null
        }

        if (consentData?.accepted) {
            return await loadAgreement(consentData.agreementId!)
        }
        return await loadActualDocumentVersion(agreementType)
    }

    const saveConsentsIfProvided = async (org: Org, userId: UserId, agreements: Agreements) => {
        return (
            await Promise.all(
                Object.keys(agreements).map(async (type) => {
                    const consent = org.consents?.[type as AgreementType]
                    if (!consent?.accepted || consent?.consentId) {
                        return null
                    }

                    await orgsService.saveConsentForOrg(org.id, userId, agreements[type as AgreementType]!)
                    return { [`signed-${type}`]: true }
                })
            )
        )
            .filter(Boolean)
            .reduce(merge, {})
    }

    const orderJobFeeCredits = async (orgId: OrgId, amount: number, redirectUrl: string): Promise<string> => {
        const response = await $api.post<string>(`jobs/${orgId}/order`, { amount, redirectUrl })
        trackCheese('job_credits_checkout_started', { org_id: orgId, credits: amount })
        return response.data
    }

    const getJobDuplicateData = async (jobId: JobId) => {
        const response = await $api.get<{ data: JobFormData; org: Org['slug'] }>(`jobs/${jobId}/duplicate`)
        return response.data
    }

    return {
        deleteJobFromCollection,
        archiveJob,
        restoreJob,
        searchJobs,
        searchJobsSemanticaly,
        searchJobsPureSemantic,
        loadJobsFacets,
        loadJobOrganizations,
        getFeaturedJobs,
        getFeaturedGovernmentJobs,
        isJobTypePaidService,
        isJobTypeInternship,
        isJobFeeTypeAdvertising,
        isJobFlexi,
        isJobTypePaid,
        hasJobSalarySet,
        formatJobSalary,
        jobSalaryRate,
        formatStartDate,
        formatEndDate,
        loadJob,
        loadJobBySlugOrId,
        loadJobDrafts,
        listJobsByOrgId,
        upsertJob,
        updateJob,
        updateJobVisibility,
        updateJobNotifiableMembers,
        createJob,
        updateJobNotifiableMembersFromToken,
        makeEmptyJob,
        loadJobRequests,
        getNotifiableMembersOfJob,
        getDataFromJobNotificationMailToken,
        loadContract,
        saveConsentsIfProvided,
        orderJobFeeCredits,
        getJobDuplicateData,
        JOBS_STORAGE_KEY
    }
}
